'use client';

// Cursor: contexto do treinador principal do aluno; só preenchido para customer. Regras em main-coach.mdc
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { actionGetCoaches } from '@/actions/workout/customer/coaches';
import { getMainCoachId, setMainCoachId as persistMainCoachId } from '@/lib/main-coach';
import { useUser } from '@/components/user/user-provider';

export type CoachItem = {
  id: number;
  name: string;
  email: string;
  photo?: string | null;
};

type MainCoachContextType = {
  /** ID do treinador principal (null se coach ou ainda não definido) */
  mainCoachId: number | null;
  /** Define o treinador principal e persiste no localStorage */
  setMainCoachId: (id: number) => void;
  /** Objeto do treinador principal (undefined se não houver ou não for customer) */
  mainCoach: CoachItem | null;
  /** Lista de treinadores do aluno (vazia se coach ou erro) */
  coaches: CoachItem[];
  isLoading: boolean;
  refetch: () => Promise<void>;
};

const MainCoachContext = createContext<MainCoachContextType | null>(null);

export function useMainCoach(): MainCoachContextType {
  const ctx = useContext(MainCoachContext);
  if (ctx === null) {
    throw new Error('useMainCoach must be used within MainCoachProvider');
  }
  return ctx;
}

/** Retorna o context ou null se fora do provider (ex.: área pública) */
export function useMainCoachOptional(): MainCoachContextType | null {
  return useContext(MainCoachContext);
}

export function MainCoachProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const isCoach = user && !('error' in user) && user.is_coach;
  const [coaches, setCoaches] = useState<CoachItem[]>([]);
  const [mainCoachId, setMainCoachIdState] = useState<number | null>(() =>
    typeof window !== 'undefined' ? getMainCoachId() : null
  );
  const [isLoading, setIsLoading] = useState(true);

  const setMainCoachId = useCallback((id: number) => {
    persistMainCoachId(id);
    setMainCoachIdState(id);
  }, []);

  const refetch = useCallback(async () => {
    if (isCoach) {
      setCoaches([]);
      setMainCoachIdState(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await actionGetCoaches();
      if (!res.success || !res.data) {
        setCoaches([]);
        setMainCoachIdState(null);
        return;
      }
      const list = res.data as CoachItem[];
      setCoaches(list);

      const storedId = getMainCoachId();
      const validStored = storedId != null && list.some((c) => c.id === storedId);

      if (list.length === 1) {
        // Um único treinador: sempre usá-lo como principal
        setMainCoachId(list[0].id);
        persistMainCoachId(list[0].id);
      } else if (validStored) {
        setMainCoachIdState(storedId);
      } else {
        // Vários treinadores e nenhum principal válido: limpar para forçar seleção
        setMainCoachIdState(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isCoach]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const mainCoach =
    mainCoachId != null ? coaches.find((c) => c.id === mainCoachId) ?? null : null;

  const value: MainCoachContextType = {
    mainCoachId,
    setMainCoachId,
    mainCoach,
    coaches,
    isLoading,
    refetch,
  };

  return (
    <MainCoachContext.Provider value={value}>
      {children}
    </MainCoachContext.Provider>
  );
}
